import type { BillingAccessState } from '../../lib/billing';

type TrialStatusBadgeProps = {
  billingState?: BillingAccessState | null;
  billingReady?: boolean;
  demoModeEnabled?: boolean;
  onOpenSettings?: () => void;
};

type BillingStateLike = BillingAccessState & {
  status?: string | null;
  current_period_end?: string | null;
  trialEnd?: string | null;
  trial_end?: string | null;
  trialEndsAt?: string | null;
};

type TrialStatusBadgeModel = {
  kind: 'trial' | 'demo' | 'pending' | 'unavailable';
  label: string;
  detail: string | null;
  ariaLabel: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function normalizeStatus(value: unknown): string {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function isTrialStatus(value: unknown): boolean {
  const normalized = normalizeStatus(value);
  if (!normalized) return false;
  if (normalized === 'trial' || normalized === 'trialing') return true;
  if (normalized.startsWith('trial') && !normalized.includes('expired') && !normalized.includes('ended') && !normalized.includes('cancel') && !normalized.includes('inactive')) {
    return true;
  }
  return normalized.includes('trial') && !normalized.includes('expired') && !normalized.includes('ended') && !normalized.includes('cancel') && !normalized.includes('inactive');
}

function getTrialEndDate(billingState?: BillingStateLike | null): Date | null {
  if (!billingState) return null;

  const candidates = [
    billingState.currentPeriodEnd,
    billingState.current_period_end,
    billingState.trialEnd,
    billingState.trial_end,
    billingState.trialEndsAt,
  ];

  for (const candidate of candidates) {
    const parsed = parseDate(candidate);
    if (parsed) return parsed;
  }

  return null;
}

function buildTrialStatusBadgeModel(
  billingState?: BillingStateLike | null,
  options: { billingReady?: boolean; demoModeEnabled?: boolean } = {},
  now = new Date(),
): TrialStatusBadgeModel | null {
  if (options.demoModeEnabled) {
    return {
      kind: 'demo',
      label: 'Demo',
      detail: null,
      ariaLabel: 'Demo mode',
    };
  }

  const statusCandidates = [billingState?.subscriptionStatus, billingState?.status];
  const isTrialLike = statusCandidates.some((value) => isTrialStatus(value));
  const trialEndDate = getTrialEndDate(billingState);
  const hasFutureOrSameDayTrialEnd = !!trialEndDate && startOfLocalDay(trialEndDate).getTime() >= startOfLocalDay(now).getTime();

  if (isTrialLike || hasFutureOrSameDayTrialEnd) {
    const baseLabel = 'Free trial';

    if (!trialEndDate) {
      return {
        kind: 'trial',
        label: baseLabel,
        detail: null,
        ariaLabel: baseLabel,
      };
    }

    const dayDiff = Math.round((startOfLocalDay(trialEndDate).getTime() - startOfLocalDay(now).getTime()) / DAY_MS);

    if (dayDiff <= 0) {
      return {
        kind: 'trial',
        label: baseLabel,
        detail: 'Trial ends today',
        ariaLabel: `${baseLabel}, Trial ends today`,
      };
    }

    if (dayDiff === 1) {
      return {
        kind: 'trial',
        label: baseLabel,
        detail: '1 day left',
        ariaLabel: `${baseLabel}, 1 day left`,
      };
    }

    return {
      kind: 'trial',
      label: baseLabel,
      detail: `${dayDiff} days left`,
      ariaLabel: `${baseLabel}, ${dayDiff} days left`,
    };
  }

  if (billingState?.hasAccess === false) {
    return {
      kind: 'unavailable',
      label: 'Limited access',
      detail: null,
      ariaLabel: 'Limited access',
    };
  }

  if (options.billingReady === false) {
    return {
      kind: 'pending',
      label: 'Billing pending',
      detail: null,
      ariaLabel: 'Billing pending',
    };
  }

  if (!billingState) {
    return {
      kind: 'unavailable',
      label: 'Billing unavailable',
      detail: null,
      ariaLabel: 'Billing unavailable',
    };
  }

  const normalizedStatus = normalizeStatus(billingState.subscriptionStatus || (billingState as BillingStateLike).status);
  const knownStatuses = new Set(['active', 'inactive', 'canceled', 'cancelled', 'paused', 'pastdue', 'past_due', 'unpaid', 'incomplete', 'incompleteexpired', 'incomplete_expired', 'graceperiod', 'grace_period', 'suspended']);

  if (!knownStatuses.has(normalizedStatus)) {
    return {
      kind: 'unavailable',
      label: 'Billing unavailable',
      detail: null,
      ariaLabel: 'Billing unavailable',
    };
  }

  return null;
}

export default function TrialStatusBadge({ billingState, billingReady = false, demoModeEnabled = false, onOpenSettings }: TrialStatusBadgeProps) {
  const model = buildTrialStatusBadgeModel(billingState as BillingStateLike | null, { billingReady, demoModeEnabled });
  if (!model) return null;

  const sharedClassName = 'inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold tracking-[0.01em] transition-colors';
  const toneClassName = (() => {
    switch (model.kind) {
      case 'demo':
        return 'border-amber-200/70 bg-amber-50/85 text-amber-800 shadow-sm dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100';
      case 'pending':
      case 'unavailable':
        return 'border-slate-200/80 bg-slate-50/85 text-slate-700 shadow-sm dark:border-slate-700/70 dark:bg-slate-800/70 dark:text-slate-200';
      case 'trial':
      default:
        return 'border-sky-200/70 bg-sky-50/80 text-sky-800 shadow-sm dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-100';
    }
  })();
  const interactiveClassName = onOpenSettings
    ? 'cursor-pointer hover:border-sky-300 hover:bg-sky-100 dark:hover:border-sky-400/30 dark:hover:bg-sky-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900'
    : '';

  const content = (
    <>
      <span className="h-1.5 w-1.5 rounded-full bg-sky-500/80 ring-4 ring-sky-500/10 dark:ring-sky-400/10" aria-hidden="true" />
      <span className="font-medium">{model.label}</span>
      {model.detail ? <span className="text-current/55">·</span> : null}
      {model.detail ? <span>{model.detail}</span> : null}
    </>
  );

  if (onOpenSettings) {
    return (
      <button
        type="button"
        onClick={onOpenSettings}
        aria-label={model.ariaLabel}
        title={model.kind === 'demo' ? 'Demo mode' : 'Open billing settings'}
        className={`${sharedClassName} ${toneClassName} ${interactiveClassName}`}
      >
        {content}
      </button>
    );
  }

  return (
    <span aria-label={model.ariaLabel} className={`${sharedClassName} ${toneClassName}`}>
      {content}
    </span>
  );
}
